import { useGetMe } from 'api-hooks/auth/query';
import { GuestModel } from 'api-hooks/guest';
import { HotelSaleModel, HotelSaleMutationInput } from 'api-hooks/sale/hotel';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { CustomLabel } from 'components/elements/custom-form-control';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import useCreateGuestAction from 'legacy-modules/master/guest/hooks/use-create-guest-action';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import ParentSaleRelatedTable from 'legacy-modules/sales/component/parent-children-sales-table';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import SalesActionComponent from 'legacy-modules/sales/component/sales-action-component';
import RescheduleHistoryTable from 'legacy-modules/sales/reschedule/reschedule-history-table';
import {
  guestNameTransformer,
  guestPreferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  Container,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import {
  HotelSaleFormSchema,
  HotelSaleFormType,
  HotelSaleGuestItemFormType,
  HotelSaleHotelSalesItemFormType,
} from './form-type';
import useGetDefaultValues, {
  addNewGuestItem,
} from './hooks/use-get-default-values';
import HotelSection from './hotel';
import HotelSaleInvoiceSelectInput from './invoice-select-input';
import SalesDetailCard from './sales-detail-card';
import SummaryHotelSale from './summary';
import { FormContent, FormDivider, OtherContainer } from '../style';

interface Props {
  hotelSale?: HotelSaleModel;
  onSubmit: (
    input: HotelSaleMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<HotelSaleModel | undefined>;
  renderError?: React.ReactNode;
  defaultValues?: HotelSaleFormType;
}

export default function HotelSaleForm(props: Props) {
  const { t } = useTranslation();
  const { hotelSale, renderError } = props;
  const { can } = useAuthorization();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    defaultValues: props.defaultValues,
    hotelSale,
  });

  const resolver = useYupValidationResolver(HotelSaleFormSchema());

  const methods = useForm<HotelSaleFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: HotelSaleFormType) => {
      const {
        hotelSales,
        tempTransactionAt,
        customerBillingOptions,
        address,
        phone,
        hasDueReceivable,
        customerStatus,
        remainingTransactionQuota,
        isExceedingQuota,
        //remove unused code
        ...rest
      } = values;
      try {
        const hs = hotelSales.map((hs) => ({
          ...hs,
          hotelSaleGuests: hs.hotelSaleGuests.map((hsg) => ({
            ...hsg,
            preferences: (hsg.preferences || []).map(
              (preference) => preference.id,
            ),
          })),
        }));

        const needApproval =
          values.transactionAt!.getTime() !== tempTransactionAt!.getTime();

        const result = {
          ...rest,
          hotelSales: hs,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const isContribute =
    hotelSale?.admin.id === employee?.id ||
    hotelSale?.salesman?.id === employee?.id ||
    hotelSale?.operator?.id === employee?.id;

  const core = can(AuthorizationRules.SalesCore);

  const onRenderComponent = hotelSale && (isContribute || core) && (
    <>
      <SalesActionComponent
        data={hotelSale}
        defaultValues={defaultValues}
        type="hotel-sales"
      />
    </>
  );

  const { getValues, setValue } = methods;
  const onSuccessCreatGuest = React.useCallback(
    (guest: GuestModel) => {
      const guestName = guestNameTransformer(guest);
      const guestPreferences = guestPreferencesTransformer(guest);
      const hotelSales: HotelSaleHotelSalesItemFormType[] = getValues(
        'hotelSales',
      ).map((hs) => {
        const hotelSaleGuests: HotelSaleGuestItemFormType[] = [
          ...hs.hotelSaleGuests,
          {
            ...addNewGuestItem(),
            guestName,
            guestId: guest.id,
            guestPreferences,
          },
        ];
        return {
          ...hs,
          hotelSaleGuests,
        };
      });
      setValue('hotelSales', hotelSales);
    },
    [getValues, setValue],
  );
  const { createGuestComponent } = useCreateGuestAction({
    onSuccess: onSuccessCreatGuest,
  });

  const onRelatedInvoiceAfterChange = React.useCallback(
    (value: HotelSaleModel) => {
      setValue('parentRefId', value.refId);
      setValue('operatorId', value.operator?.id || '');
      setValue('salesmanId', value.salesman?.id || '');
      setValue('customerId', value.customer?.id || '');
      setValue('customerBillingId', value.customerBillingAddress?.id || '');
    },
    [setValue],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!hotelSale}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        hotelSale && `${t('common:hotel_sale')} - ${hotelSale.invoiceNumber}`
      }
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={hotelSale}
              noSubmit
              noCancel
              noEdit
              noDelete
              title={
                hotelSale ? (
                  <TitleStatus
                    type="sale-default"
                    isCompleted={hotelSale.isCompleted}
                    title={`${t('common:hotel_sale')} - ${
                      hotelSale.invoiceNumber
                    }`}
                    status={hotelSale.status}
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:hotel_sale') })
                )
              }
              onRenderComponent={
                editable ? createGuestComponent : onRenderComponent
              }
            />
          )}
        </FormState>
        {renderError}
        <div>
          {hotelSale && !!(hotelSale?.releaseSales || []).length && (
            <SalePurchaseRefundTable data={hotelSale.releaseSales} />
          )}
          {hotelSale &&
            !!hotelSale.purchases.length &&
            (isContribute || core) && (
              <PurchaseRelatedTable
                data={hotelSale.purchases}
                navigation="HotelPurchasesView"
              />
            )}
          {hotelSale && (
            <RescheduleHistoryTable saleId={hotelSale.id} type="hotel" hide />
          )}
          {hotelSale &&
            (!!hotelSale.parentSale ||
              !!(hotelSale.childrenSales || []).length) && (
              <ParentSaleRelatedTable
                data={hotelSale}
                navigation="HotelSalesView"
              />
            )}
          <RawSectionContainer>
            <HalfContainer>
              <HotelSaleInvoiceSelectInput
                label={t('common:related_invoice')}
                placeholder={t('common:choose_invoice')}
                name="parentRefId"
                onAfterDetailChange={onRelatedInvoiceAfterChange}
                isSales
              />
            </HalfContainer>
          </RawSectionContainer>
          <RawSectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketSales:group_code')}
                placeholder={t('ticketSales:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </RawSectionContainer>
          <SeparatorVertical $margin={16} />
          <SalesDetailCard hotelSale={hotelSale} />
          <SeparatorVertical $margin={46}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <HotelSection />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <Container $style={{ height: 'auto' }}>
            <OtherContainer>
              <CustomLabel label={t('common:discount')} />
              <Input
                type="number"
                name="discount"
                placeholder={t('common:discount')}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
            <SeparatorVertical $margin={16} />
            <OtherContainer>
              <CustomLabel label={t('common:other')} />
              <Input
                type="number"
                name="otherCost"
                placeholder={t('common:other')}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
          </Container>
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryHotelSale hotelSale={hotelSale} />
    </Form>
  );
}
